import logger from 'ws-scripts/modules/logger';
import {
	MAX_AUTO_ZOOM,
	MIN_AUTO_ZOOM,
	SINGLE_MARKER_ZOOM
} from '../constants/mapConstants';

/**
 * Create Google Maps markers for locations with valid coordinates.
 * @param {google.maps.Map} map - The Google Maps instance
 * @param {Array} locations - Array of location/tile objects
 * @param {Object} icons - Marker icon configurations
 * @param {Function} handleMarkerClick - Callback for marker click events
 * @param {React.RefObject<number>} hoveredTileIndexRef - Ref to the currently hovered tile index
 * @returns {google.maps.Marker[]} - Array of created markers
 */
export const createMarkers = (
	map,
	locations,
	icons,
	handleMarkerClick,
	hoveredTileIndexRef
) => {
	const markers = [];

	locations.forEach((tile, index) => {
		const location = tile.location || tile;

		if (!location.latitude || !location.longitude) {
			return;
		}

		const lat = parseFloat(location.latitude);
		const lng = parseFloat(location.longitude);
		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			return;
		}

		try {
			const marker = new window.google.maps.Marker({
				position: { lat, lng },
				map,
				title: location.name || `Location ${index + 1}`,
				icon: icons.default
			});

			if (marker) {
				// Pin hover only applies when no tile is actively hovered
				marker.addListener('mouseover', () => {
					if (hoveredTileIndexRef.current === -1) {
						marker.setIcon(icons.active);
					}
				});

				marker.addListener('mouseout', () => {
					if (hoveredTileIndexRef.current === -1) {
						marker.setIcon(icons.default);
					}
				});

				marker.addListener('click', () => {
					handleMarkerClick(location, marker);
				});

				markers.push(marker);
			}
		} catch (error) {
			logger.warn('Marker creation failed', error);
		}
	});

	return markers;
};

/**
 * Fit the map viewport to show all markers.
 * - Multiple markers → fitBounds with padding, clamped to MAX_AUTO_ZOOM
 * - Single marker   → center on it at SINGLE_MARKER_ZOOM
 * @param {google.maps.Map} map - The Google Maps instance
 * @param {google.maps.Marker[]} markers - Array of markers to fit
 */
export const fitMapBounds = (map, markers) => {
	if (markers.length > 1) {
		const bounds = new window.google.maps.LatLngBounds();
		markers.forEach((marker) => {
			bounds.extend(marker.getPosition());
		});

		// Asymmetric padding so pin graphics (anchored at their tip) are
		// not clipped at the top edge of the map viewport. Top padding
		// (80px) clears the tallest pin state (active/hover ~54px) with
		// buffer to spare.
		const padding = { top: 80, right: 40, bottom: 40, left: 40 };
		const currentBounds = map.getBounds();
		if (
			currentBounds &&
			currentBounds.contains(bounds.getNorthEast()) &&
			currentBounds.contains(bounds.getSouthWest())
		) {
			map.panToBounds(bounds, padding);
		} else {
			map.fitBounds(bounds, padding);
		}

		// Clamp zoom to [MIN_AUTO_ZOOM, MAX_AUTO_ZOOM] once the map finishes adjusting
		window.google.maps.event.addListenerOnce(map, 'idle', () => {
			if (map) {
				const currentZoom = map.getZoom();
				if (currentZoom > MAX_AUTO_ZOOM) {
					map.setZoom(MAX_AUTO_ZOOM);
				} else if (currentZoom < MIN_AUTO_ZOOM) {
					map.setZoom(MIN_AUTO_ZOOM);
				}
			}
		});
	} else if (markers.length === 1) {
		map.setCenter(markers[0].getPosition());
		map.setZoom(SINGLE_MARKER_ZOOM);
	}
};
